import React, { useContext, useMemo } from 'react';
import { css } from '@emotion/react';
import { Grid, Switch, Tooltip } from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { SectionTitle, PrimaryButton, Body } from '@/styles/blocks';
import { useClientUser } from '@/hooks';
import { SHIPMENT_FORM_TYPES } from '../constants';
import { Context } from '../store';
import OrderCard from '../cards/OrderCard';
import PickUpCard from '../cards/PickUpCard';
import { InfoCircleOutlined } from '@ant-design/icons';
import FreightTypeCard from '../cards/FreightTypeCard';
import { genAttributes } from '@onward-delivery/core';
import PickUpGroupCard from '../cards/PickUpGroupCard';

// Stable reference for the `marketplaceOverride` prop — an inline `={{}}` would
// bust React.memo(OrderCard) on every render. Shared by all OrderCards without
// a real marketplace override.
const EMPTY_OBJECT = Object.freeze({});

const ReviewOrders = ({ filter = 'all' }) => {
    const { state, errors, callbacks } = useContext(Context);
    const { circles } = useClientUser();

    const sorted = useMemo(() => {
        const all =
            state.orders && Object.values(state.orders).length > 0
                ? Object.values(state.orders).sort((l, r) => l.key - r.key)
                : [];

        // "Errors" filter (mirrors JobDetails): keep only orders flagged with
        // missing/invalid required fields. Keyed by `order.key` (not yet persisted).
        if (filter === 'errors') {
            return all.filter((order) =>
                Object.values(errors.missingFieldsByOrder?.[order.key] || {}).some((x) => x)
            );
        }

        return all;
    }, [state.orders, filter, errors.missingFieldsByOrder]);

    const groupedByPickup = useMemo(() => {
        if (state.groupedPickup) {
            // Build groups from the already-filtered `sorted` list so the
            // grouped view honors the filter and empty pickup groups drop out.
            return sorted.reduce((acc, order) => {
                const { address: pu_address } = genAttributes(order, true);
                const address = order[pu_address];
                return {
                    ...acc,
                    [address]: [...(acc[address] || []), order],
                };
            }, {});
        }

        return {};
    }, [sorted, state.groupedPickup]);

    // Memoize the validation closures so the prop reference stays stable across
    // renders that don't change the error map (keeps React.memo(OrderCard)
    // effective).
    const groupedValidation = useMemo(
        () => ({
            hasError: (order) => Object.values(errors.missingFieldsByOrder?.[order.key] || {}).some((x) => x),
            hasErrorTab: (order, tab) => !!errors.missingFieldsByOrder?.[order.key]?.[tab],
            missingTabs: (order) => {
                const tabs = errors.missingFieldsByOrder?.[order.key] || {};
                return Object.entries(tabs)
                    .filter(([, hasError]) => !!hasError)
                    .map(([tab]) => tab);
            },
        }),
        [errors.missingFieldsByOrder]
    );

    // Per-order validation lookup for the non-grouped path, keyed by order so
    // each card gets a stable reference until its error tabs change.
    const flatValidationByKey = useMemo(() => {
        const result = {};
        for (const order of sorted) {
            const tabs = errors.missingFieldsByOrder?.[order.key] || EMPTY_OBJECT;
            result[order.key] = {
                hasError: () => Object.values(tabs).some((x) => x),
                hasErrorTab: (tab) => !!tabs[tab],
                missingTabs: () =>
                    Object.entries(tabs)
                        .filter(([, hasError]) => !!hasError)
                        .map(([tab]) => tab),
            };
        }
        return result;
    }, [sorted, errors.missingFieldsByOrder]);

    return (
        <>
            <Grid
                item
                container
                css={css`
                    margin-bottom: 16px;
                `}
            >
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 1;
                        margin-right: 20px;
                    `}
                >
                    <Grid
                        item
                        css={css`
                            margin-bottom: 8px;
                        `}
                    >
                        <SectionTitle>Freight Type</SectionTitle>
                    </Grid>
                    <Grid item>
                        <FreightTypeCard />
                    </Grid>
                </Grid>
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 4;
                    `}
                >
                    {!state.groupedPickup && (
                        <>
                            <Grid
                                item
                                css={css`
                                    margin-bottom: 8px;
                                `}
                            >
                                <SectionTitle>Pickup / Warehouse Information</SectionTitle>
                            </Grid>
                            <Grid item>
                                <PickUpCard />
                            </Grid>
                        </>
                    )}
                </Grid>
            </Grid>
            <Grid container justifyContent="space-between">
                <Grid
                    item
                    css={css`
                        margin-bottom: 8px;
                    `}
                >
                    <SectionTitle>Orders</SectionTitle>
                </Grid>
            </Grid>
            {state.groupedPickup ? (
                <>
                    {Object.entries(groupedByPickup).map(([pu_address, pu_orders]) => (
                        <Grid item key={pu_address}>
                            <PickUpGroupCard
                                address={pu_address}
                                orders={pu_orders}
                                validation={groupedValidation}
                                callbacks={callbacks}
                            />
                        </Grid>
                    ))}
                </>
            ) : (
                <>
                    {sorted.map((order, idx) => (
                        <Grid item key={order.key}>
                            <OrderCard
                                order={order}
                                orderNumber={idx + 1}
                                validation={flatValidationByKey[order.key]}
                                marketplaceOverride={EMPTY_OBJECT}
                                callbacks={callbacks}
                            />
                        </Grid>
                    ))}
                </>
            )}
            {state.formType === SHIPMENT_FORM_TYPES.MANUAL ? (
                <Grid
                    container
                    item
                    direction="row"
                    css={css`
                        margin-top: 24px;
                    `}
                >
                    <Grid
                        item
                        css={css`
                            margin-right: 16px;
                        `}
                    >
                        <PrimaryButton onClick={() => callbacks.addOrder(state.pickupInfo?.order_type || 'delivery')}>
                            <span>
                                <Add />
                                Order
                            </span>
                        </PrimaryButton>
                    </Grid>
                </Grid>
            ) : null}
        </>
    );
};

export default ReviewOrders;
